/**
 * Initial Outlay for the Max Affordability Calculator — the down-payment schedule
 * and closing fees per property type, shared by the on-screen Cash & Mortgage
 * section and the PDF report. Mirrors the Buying Calculator's Initial Outlay (its
 * defaults) so both calculators quote the same total for the same price.
 *
 * - Private / EC (new launch, Normal Payment Scheme): OTP 5% + S&P 15% are paid
 *   upfront. The rest of the down payment (Foundation onwards) falls later in the
 *   progressive schedule.
 * - HDB resale: Option Fee + Exercise Fee on the OTP (paid in cash), then the rest
 *   of the down payment at completion — no progressive schedule, all upfront.
 */

export type AffordabilityPropertyType = 'private' | 'ec' | 'hdb';

/** New-launch down-payment stages paid upfront (the Buying Calculator's Initial Outlay): booking fee, S&P. */
export const OTP_STAGE_PCT = 5;
export const SP_STAGE_PCT = 15;

// HDB resale OTP deposit, paid in cash: Option Fee (up to $1,000) on grant of the OTP,
// then Exercise Fee (up to $4,000) on exercise — same defaults as the Buying Calculator.
export const HDB_OPTION_FEE = 1000;
export const HDB_EXERCISE_FEE = 4000;

// Closing fees — the Buying Calculator's defaults. Conveyancing applies to every type;
// the rest are HDB resale only, and the caveat only on an HDB concessionary loan.
export const LEGAL_FEE = 3000;
export const HDB_CAVEAT_FEE = 200;
export const HDB_VALUATION_FEE = 120;
export const HDB_ADMIN_FEE = 80;
/** Agency service fee: commission rate (before GST) on the price, plus 9% GST. */
export const HDB_AGENCY_FEE_PCT = 1;
export const AGENCY_FEE_GST = 1.09;

export interface ClosingFee {
  /** Section in the Buying Calculator's Initial Outlay table. */
  group: 'legal' | 'other';
  label: string;
  amount: number;
}

/** Fees the Buying Calculator adds to its Initial Outlay, in its row order. */
export function getClosingFees(
  type: AffordabilityPropertyType,
  price: number,
  hdbLoan: boolean,
): ClosingFee[] {
  const fees: ClosingFee[] = [{ group: 'legal', label: 'Conveyancing', amount: LEGAL_FEE }];
  if (type !== 'hdb') return fees;
  if (hdbLoan) fees.push({ group: 'legal', label: 'Caveat Fee', amount: HDB_CAVEAT_FEE });
  fees.push(
    { group: 'other', label: 'Valuation Fee', amount: HDB_VALUATION_FEE },
    { group: 'other', label: 'HDB Admin Fee', amount: HDB_ADMIN_FEE },
    {
      group: 'other',
      label: `Agency Service Fee (${HDB_AGENCY_FEE_PCT}% + 9% GST)`,
      amount: Math.round(price * (HDB_AGENCY_FEE_PCT / 100) * AGENCY_FEE_GST),
    },
  );
  return fees;
}

export interface DownPaymentPlan {
  stages: { label: string; amount: number }[];
  /** Paid before the bank starts disbursing (sum of `stages`). */
  upfront: number;
  upfrontPct: number;
  /** Down payment still owed later in the progressive schedule (Private / EC). */
  deferred: number;
  deferredPct: number;
}

export function getDownPaymentPlan(
  type: AffordabilityPropertyType,
  price: number,
  requiredLoan: number,
  ltv: number,
): DownPaymentPlan {
  const totalDown = Math.max(0, price - requiredLoan);

  if (type === 'hdb') {
    const optionFee = Math.min(HDB_OPTION_FEE, totalDown);
    const exerciseFee = Math.min(HDB_EXERCISE_FEE, totalDown - optionFee);
    return {
      stages: [
        { label: 'Option Fee (grant of OTP)', amount: optionFee },
        { label: 'Exercise Fee (exercise of OTP)', amount: exerciseFee },
        { label: 'Balance at completion', amount: totalDown - optionFee - exerciseFee },
      ],
      upfront: totalDown,
      upfrontPct: 100 - ltv,
      deferred: 0,
      deferredPct: 0,
    };
  }

  const otp = Math.round(price * OTP_STAGE_PCT / 100);
  const sp = Math.round(price * SP_STAGE_PCT / 100);
  const upfront = otp + sp;
  const upfrontPct = OTP_STAGE_PCT + SP_STAGE_PCT;
  // The rest of the down payment (5% at 75% LTV, 25% at 55%) is paid from Foundation onwards.
  const deferredPct = Math.max(0, 100 - ltv - upfrontPct);
  return {
    stages: [
      { label: `OTP (Option Fee ${OTP_STAGE_PCT}%)`, amount: otp },
      { label: `Pay (${SP_STAGE_PCT}%)`, amount: sp },
    ],
    upfront,
    upfrontPct,
    deferred: deferredPct > 0 ? Math.max(0, totalDown - upfront) : 0,
    deferredPct,
  };
}
